import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import dayjs from 'dayjs';

import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { useSocket } from '../../contexts/SocketContext';
import DeveloperOAuthApps from './DeveloperOAuthApps';
import DeveloperApiProviders from './DeveloperApiProviders';

export default function Developer() {
  const dispatch = useDispatch();
  const socket = useSocket();

  const [syncLoading, setSyncLoading] = useState(false);
  const [mongoSyncResult, setMongoSyncResult] = useState(null);
  const [mongoSyncError, setMongoSyncError] = useState('');
  const [postgresSyncResult, setPostgresSyncResult] = useState(null);
  const [postgresSyncError, setPostgresSyncError] = useState('');
  const [syncConfirm, setSyncConfirm] = useState(false);

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineLastFetch, setOnlineLastFetch] = useState(null);

  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const fetchOnlineUsers = useCallback(async () => {
    setOnlineLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/user/online`);
      setOnlineUsers(Array.isArray(data) ? data : []);
      setOnlineLastFetch(Date.now());
    } catch {
      // silently ignore
    } finally {
      setOnlineLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchOnlineUsers();
    const id = setInterval(fetchOnlineUsers, 30 * 1000); // fallback poll ทุก 30 วินาที
    return () => clearInterval(id);
  }, [fetchOnlineUsers]);

  // real-time update ผ่าน Socket.IO
  useEffect(() => {
    if (!socket) return;
    const handler = (list) => {
      setOnlineUsers(Array.isArray(list) ? list : []);
      setOnlineLastFetch(Date.now());
    };
    socket.on('online:update', handler);
    return () => socket.off('online:update', handler);
  }, [socket]);

  const runSync = async () => {
    setSyncConfirm(false);
    setSyncLoading(true);
    setMongoSyncResult(null);
    setMongoSyncError('');
    setPostgresSyncResult(null);
    setPostgresSyncError('');

    // ยิงพร้อมกัน — คนละ database คนละ service กัน ถ้าตัวใดตัวหนึ่ง fail (เช่น
    // DATABASE_DEV_URL ยังไม่ได้ตั้งค่า) อีกตัวก็ยัง sync ต่อได้ตามปกติ
    const [mongoOutcome, postgresOutcome] = await Promise.allSettled([
      api.post(`${apiBase}/sync-dev-db`),
      dispatch(actions.syncAccountingDevDb()),
    ]);

    if (mongoOutcome.status === 'fulfilled') {
      setMongoSyncResult(mongoOutcome.value.data);
    } else {
      setMongoSyncError(
        mongoOutcome.reason?.response?.data?.error?.message
          || mongoOutcome.reason?.message
          || 'Sync Mongo ไม่สำเร็จ',
      );
    }

    if (postgresOutcome.status === 'fulfilled') {
      setPostgresSyncResult(postgresOutcome.value);
    } else {
      setPostgresSyncError(postgresOutcome.reason?.message || 'Sync Postgres ไม่สำเร็จ');
    }

    setSyncLoading(false);
  };

  const renderSyncDevDb = () => (
    <Card sx={{ mb: 3, border: '1px solid #fca5a5' }} variant="outlined">
      <CardHeader
        title="Sync Dev Database"
        subheader="คัดลอกข้อมูลจาก Production → Dev ทั้ง MongoDB (Atlas coreplan_dev) และ Postgres (accounting-service) — ข้อมูลเดิมใน Dev จะถูกลบทั้งหมด"
        titleTypographyProps={{ color: '#dc2626' }}
      />
      <CardContent>
        {mongoSyncError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Mongo: {mongoSyncError}
          </Typography>
        )}
        {postgresSyncError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Postgres: {postgresSyncError}
          </Typography>
        )}

        {mongoSyncResult && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#15803d' }}>
              Mongo sync สำเร็จ — {mongoSyncResult.total?.toLocaleString()} documents
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Collection</TableCell>
                    <TableCell align="right">Documents</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(mongoSyncResult.collections || []).map((r) => (
                    <TableRow key={r.collection}>
                      <TableCell>{r.collection}</TableCell>
                      <TableCell align="right">{r.count?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {postgresSyncResult && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#15803d' }}>
              Postgres sync สำเร็จ — {postgresSyncResult.total?.toLocaleString()} rows
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Table</TableCell>
                    <TableCell align="right">Rows</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(postgresSyncResult.tables || []).map((r) => (
                    <TableRow key={r.table}>
                      <TableCell>{r.table}</TableCell>
                      <TableCell align="right">{r.count?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Button
          variant="contained"
          color="error"
          disabled={syncLoading}
          onClick={() => setSyncConfirm(true)}
          startIcon={syncLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {syncLoading ? 'กำลัง Sync...' : 'เริ่ม Sync'}
        </Button>
      </CardContent>
    </Card>
  );

  const renderOnlineUsers = () => {
    const onlineList = onlineUsers.filter((u) => u.isOnline);
    const recentList = onlineUsers.filter((u) => !u.isOnline);
    const totalOnline = onlineList.length;

    return (
      <Card sx={{ mb: 3 }} variant="outlined">
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <FiberManualRecordIcon sx={{ color: totalOnline > 0 ? '#22c55e' : '#94a3b8', fontSize: 14 }} />
              <Typography variant="h6" component="span">
                ผู้ใช้งานในระบบ
              </Typography>
              {totalOnline > 0 && (
                <Chip label={`${totalOnline} online`} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.72rem' }} />
              )}
            </Stack>
          }
          subheader="แสดงเฉพาะผู้ที่มี request จริง (active < 3 นาที) และเคย active ใน 1 ชั่วโมงที่ผ่านมา"
          action={
            <Stack direction="row" alignItems="center" spacing={1}>
              {onlineLastFetch && (
                <Typography variant="caption" color="text.secondary">
                  อัปเดต {dayjs(onlineLastFetch).format('HH:mm:ss')}
                </Typography>
              )}
              <Button size="small" onClick={fetchOnlineUsers} disabled={onlineLoading}>
                {onlineLoading ? <CircularProgress size={14} /> : 'รีเฟรช'}
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {onlineUsers.length === 0 && !onlineLoading && (
            <Typography variant="body2" color="text.secondary">ไม่มีผู้ใช้งานในขณะนี้</Typography>
          )}

          {/* กำลัง online */}
          {onlineList.length > 0 && (
            <Box sx={{ mb: recentList.length > 0 ? 2 : 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#15803d', mb: 0.5, display: 'block' }}>
                กำลังใช้งาน
              </Typography>
              <Stack spacing={0.5}>
                {onlineList.map((u) => (
                  <Box
                    key={u.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#f0fdf4',
                      borderRadius: 1.5,
                      border: '1px solid #bbf7d0',
                    }}
                  >
                    <FiberManualRecordIcon sx={{ color: '#22c55e', fontSize: 10 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{u.username}</Typography>
                    <Tooltip title={u.ip || ''}>
                      <Typography variant="caption" color="text.secondary">
                        active เมื่อกี้
                      </Typography>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* เคย active แต่เลิกแล้ว (< 1 ชั่วโมง) */}
          {recentList.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', mb: 0.5, display: 'block' }}>
                เลิกใช้งานแล้ว
              </Typography>
              <Stack spacing={0.5}>
                {recentList.map((u) => (
                  <Box
                    key={u.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <FiberManualRecordIcon sx={{ color: '#cbd5e1', fontSize: 10 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{u.username}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.minutesAgo < 1 ? 'เมื่อกี้' : `เลิกใช้งาน ${u.minutesAgo} นาทีที่แล้ว`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      {renderSyncDevDb()}
      {renderOnlineUsers()}

      <Dialog open={syncConfirm} onClose={() => setSyncConfirm(false)}>
        <DialogTitle>ยืนยันการ Sync</DialogTitle>
        <DialogContent>
          <Typography>
            ข้อมูลทั้งหมดใน <strong>coreplan_dev</strong> (Mongo) และ dev Postgres ของ
            accounting-service จะถูกลบและแทนที่ด้วยข้อมูลจาก Production ใช่ไหม?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirm(false)}>ยกเลิก</Button>
          <Button variant="contained" color="error" onClick={runSync}>
            ยืนยัน Sync
          </Button>
        </DialogActions>
      </Dialog>

      <DeveloperOAuthApps />
      <DeveloperApiProviders />
    </div>
  );
}
